import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SalaryChangeRow, EmpSummary, QueryResponse } from '../../../services/pw9341Api';

interface ResultTableProps {
  result: QueryResponse;
}

const formatYearMonth = (ym: string): string => {
  if (ym && ym.length === 6) {
    return `${ym.substring(0, 4)}-${ym.substring(4, 6)}`;
  }
  return ym || '';
};

const detailColumns: ColumnsType<SalaryChangeRow> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 60,
    render: (code: string, record: SalaryChangeRow) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '人員',
    dataIndex: 'empCode',
    key: 'empCode',
    width: 80,
    render: (code: string, record: SalaryChangeRow) =>
      `${(code || '').trim()} ${(record.empName || '').trim()}`,
  },
  {
    title: '年月',
    dataIndex: 'yearMonth',
    key: 'yearMonth',
    width: 80,
    render: (v: string) => formatYearMonth(v),
  },
  {
    title: '序號',
    dataIndex: 'seqNo',
    key: 'seqNo',
    width: 50,
    align: 'center',
  },
  {
    title: '薪資項目',
    dataIndex: 'itemCode',
    key: 'itemCode',
    width: 100,
    render: (code: string, record: SalaryChangeRow) =>
      `${(code || '').trim()} ${(record.itemName || '').trim()}`,
  },
  {
    title: '異動前金額',
    dataIndex: 'salaryBefore',
    key: 'salaryBefore',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '異動後金額',
    dataIndex: 'salaryAfter',
    key: 'salaryAfter',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '差額',
    key: 'diff',
    width: 80,
    align: 'right',
    render: (_: unknown, record: SalaryChangeRow) => {
      const diff = (record.salaryAfter ?? 0) - (record.salaryBefore ?? 0);
      const color = diff > 0 ? 'green' : diff < 0 ? 'red' : undefined;
      return <Tag color={color}>{diff > 0 ? '+' : ''}{diff.toLocaleString()}</Tag>;
    },
  },
  {
    title: '備註',
    dataIndex: 'remark',
    key: 'remark',
    width: 120,
    ellipsis: true,
  },
  {
    title: '異動單號',
    dataIndex: 'changeNo',
    key: 'changeNo',
    width: 100,
  },
];

const summaryColumns: ColumnsType<EmpSummary> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 60,
    render: (code: string, record: EmpSummary) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '人員',
    dataIndex: 'empCode',
    key: 'empCode',
    width: 100,
    render: (code: string, record: EmpSummary) =>
      `${(code || '').trim()} ${(record.empName || '').trim()}`,
  },
  {
    title: '年度首薪',
    dataIndex: 'salaryFirst',
    key: 'salaryFirst',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '年度末薪',
    dataIndex: 'salaryLast',
    key: 'salaryLast',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '年度薪資變化',
    key: 'change',
    width: 120,
    align: 'right',
    render: (_: unknown, record: EmpSummary) => {
      const diff = (record.salaryLast ?? 0) - (record.salaryFirst ?? 0);
      const color = diff > 0 ? 'green' : diff < 0 ? 'red' : undefined;
      return <Tag color={color}>{diff > 0 ? '+' : ''}{diff.toLocaleString()}</Tag>;
    },
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.changeNo}-${row.seqNo}-${i}`,
      })),
    [result.details]
  );

  const summaryData = useMemo(
    () =>
      (result.summaries || []).map((row, i) => ({
        ...row,
        _key: `${row.deptCode}-${row.empCode}-${i}`,
      })),
    [result.summaries]
  );

  const items = [
    {
      key: 'summary',
      label: `年度薪資彙總 (${summaryData.length})`,
      children: (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey="_key"
          size="small"
          scroll={{ x: 500, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
    {
      key: 'details',
      label: `異動明細 (${result.totalCount})`,
      children: (
        <Table
          columns={detailColumns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 1000, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
